import React from 'react';
import { useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import classnames from 'classnames';
import { ButtonStyledLikeLink, Icon, Tab } from 'xm-spark';
import { Tooltip } from '@xmatters/legacypopper';

import DockIcon from 'resources/svg/helpViewIcons/dock.svg';
import UndockIcon from 'resources/svg/helpViewIcons/undock.svg';
import { HELP_TABS } from 'xm-constants/helpView';
import { hideHelp, setCurrentTab } from 'actions/helpPopover';
import { getCurrentTab, getHasQuickStartGuide } from 'selectors/helpView';
import useAction from 'hooks/useAction';

import { useHelpGeometryContext, useHelpViewDragPositioning } from '../hooks';
import { storeHelpGeometry } from '../utils';
import styles from './HelpViewHeader.module.scss';

function HelpViewTitle({ currentTab }) {
  const hasQuickStartGuide = useSelector(getHasQuickStartGuide);
  const setTab = useAction(setCurrentTab);

  if (!hasQuickStartGuide) {
    return (
      <FormattedMessage id="HELP_MENU_TITLE">
        {title => <span className={styles.title}>{title}</span>}
      </FormattedMessage>
    );
  }

  const getOnClick = tab => e => {
    e.preventDefault();
    setTab(tab);
    return false;
  };

  return (
    <div className={styles.tabs}>
      <Tab
        title={<FormattedMessage id="HELP_MENU_TITLE" />}
        testId="HelpView_tab_helpCenter"
        active={currentTab === HELP_TABS.HELP}
        onClick={getOnClick(HELP_TABS.HELP)}
        activeClassName={styles.active}
        className={styles.tab}
      />
      <Tab
        title={<FormattedMessage id="HELP_MENU_QUICK_START_TITLE" />}
        testId="HelpView_tab_quickStart"
        active={currentTab === HELP_TABS.QUICK_START}
        onClick={getOnClick(HELP_TABS.QUICK_START)}
        activeClassName={styles.active}
        className={styles.tab}
      />
    </div>
  );
}

export default function HelpViewHeader() {
  const { isHelpDrawerDocked, setIsHelpDrawerDocked, geometry } =
    useHelpGeometryContext();

  const currentTab = useSelector(getCurrentTab);
  const closePopover = useAction(hideHelp);

  return (
    <div
      className={classnames(styles.header, {
        [styles.docked]: isHelpDrawerDocked,
      })}
      {...useHelpViewDragPositioning()}
    >
      {!isHelpDrawerDocked && (
        <Icon type="icon-draggable" className={styles.dragHandle} />
      )}
      <HelpViewTitle currentTab={currentTab} />
      <div className={styles.actions}>
        <div className={styles.dockActions}>
          <Tooltip
            // Key helps close the tooltip when the menu jumps away after click:
            key={isHelpDrawerDocked ? 'undockActive' : 'undockInactive'}
            placement="bottom"
            tooltipContent={<FormattedMessage id="HELP_MENU_ACTION_UNDOCK" />}
          >
            <ButtonStyledLikeLink
              className={styles.undockToggleButton}
              onClick={() => isHelpDrawerDocked && setIsHelpDrawerDocked(false)}
              data-testid="HelpView_undock_button"
            >
              <Icon>
                <UndockIcon />
              </Icon>
            </ButtonStyledLikeLink>
          </Tooltip>
          <Tooltip
            // Key helps close the tooltip when the menu jumps away after click:
            key={isHelpDrawerDocked ? 'dockActive' : 'dockInactive'}
            placement="bottom"
            tooltipContent={<FormattedMessage id="HELP_MENU_ACTION_DOCK" />}
          >
            <ButtonStyledLikeLink
              className={styles.dockToggleButton}
              onClick={() => !isHelpDrawerDocked && setIsHelpDrawerDocked(true)}
              data-testid="HelpView_dock_button"
            >
              <Icon>
                <DockIcon />
              </Icon>
            </ButtonStyledLikeLink>
          </Tooltip>
        </div>
        <ButtonStyledLikeLink
          className={styles.closeButton}
          onClick={() => {
            closePopover();
            storeHelpGeometry(geometry);
          }}
          data-testid="HelpView_close_button"
        >
          <Icon type="icon-cancel" />
        </ButtonStyledLikeLink>
      </div>
    </div>
  );
}

HelpViewHeader.displayName = 'HelpViewHeader';
